import { FUNDING_ELIGIBILITY_CATEGORY_GROUPS } from "@/lib/apply-now/funding-eligibility-categories";
import { FUNDING_ELIGIBILITY_COUNTRIES } from "@/lib/apply-now/funding-eligibility-countries";

import type { LiveHomeCopy } from "../home/live-home-types";
import {
  HOME_DEPLOYED_CAPITAL_USD,
  HOME_SIGNUP_URL,
  HOME_TRUSTED_BUSINESSES_COUNT,
  HOME_TRUSTPILOT_URL,
} from "../home/live-home-types";

export const liveHomeCopy: LiveHomeCopy = {
  signupUrl: HOME_SIGNUP_URL,
  deployedCapitalUsd: HOME_DEPLOYED_CAPITAL_USD,
  trustedBusinessesCount: HOME_TRUSTED_BUSINESSES_COUNT,
  hero: {
    eyebrowLabel: "Capital deployed so far:",
    titleLead: "Financing built for growing businesses.",
    titleSubtitle: "Apply in less than 10 minutes.",
    descriptionLine1:
      "From $10k to $20m. No personal guarantees. No equity taken.",
    descriptionLine2: "Backed by world-leading financial institutions.",
    emailPlaceholder: "Your work email",
    ctaLabel: "Apply now",
    ratingMobile: "4.6 stars from 500+ reviews",
    ratingDesktop: "4.6 stars from 500+ reviews",
    ratingUrl: HOME_TRUSTPILOT_URL,
    ratingLinkLabel: "Read Wayflyer reviews on Trustpilot",
  },
  chapterLabels: {
    applicationProcess: "Application Process",
    terms: "Terms",
    customers: "Customers",
    underwriting: "Underwriting Process",
    faqs: "FAQs",
  },
  trustedGrid: {
    titleSuffix: "+ businesses of all sizes",
    titleShort: "Trusted by",
    ctaLabel: "Customer stories",
    ctaPath: "/our-customers",
  },
  productPreviewSteps: [
    "Tell us about your business",
    "Securely connect your platforms",
    "Get approved for financing",
  ],
  fundingEligibility: {
    headingLead:
      "Make funding your business a breathtakingly smooth experience.",
    headingEmphasis: "No, seriously.",
    step01Title: "Confirm your eligibility",
    step01Body:
      "Tell us about your business so we can confirm your eligibility.",
    step02Title: "Submit an application",
    step02TitleIneligible: "Stay in the loop",
    step02BodyBeforeComplete:
      "Provide the additional information we need to complete a financial review.",
    step02BodyMobile:
      "A focused, frictionless form. Share the essentials and move forward without delays.",
    step02BodyAfterComplete:
      "Securely connect to the Wayflyer platform so our proprietary models can assess your business performance in minutes.",
    step02BodyIneligible:
      "You're not eligible just yet, but that can change fast as you grow. Leave your email and we'll check back in.",
    ineligibleSubmitToastTitle: "Thanks for staying in touch",
    ineligibleSubmitToastDescription:
      "We'll email you when your business may qualify for funding.",
    step03Title: "Get approved for financing",
    step03TitleIneligible: "Know what we look for",
    step03Body:
      "A fast, definitive answer. Know your approved amount and unlock funding within the hour.",
    step03BodyIneligiblePrefix: "See the full ",
    eligibilityCriteriaLinkText: "eligibility criteria",
    step03BodyIneligibleSuffix: " to know exactly when you're ready.",
    eligibilityCriteriaLinkHref:
      "https://help-center.wayflyer.com/en/articles/633716-what-are-the-requirements-to-get-funding-from-wayflyer",
    estimatedFundingLabel: "Estimated funding:",
    estimatedFundingSuccessLabel: "Businesses like yours can typically access:",
    estimatedFundingUpToPrefix: "up to ",
    ineligibleMessage:
      "Financing in {country} starts at an average monthly revenue of: {threshold}/month",
    ineligibleRevenueLabel: "Your monthly revenue:",
    estimatePrompt:
      "Complete the form to get an estimate of how much you can access.",
    congratsTitle: "Congrats! Looks like you're eligible.",
    congratsSubtitle: "Looks like you're eligible.",
    emailPlaceholder: "Your email",
    ineligibleEmailPlaceholder: "Your email",
    emailLabel: "Your work email",
    applyNowLabel: "Apply now",
    ineligibleApplyLabel: "Get updates",
    sendingLabel: "Sending…",
    submitToastTitle: "Application submitted",
    submitToastDescription: "Thanks — we'll follow up at your email shortly.",
    formMonthlyRevenueLabel: "Monthly revenue",
    formMonthlyRevenueAria: "Monthly revenue",
    formMonthlyRevenuePlaceholder: "100,000",
    formCategoryLabel: "Category",
    formCategoryAria: "Category",
    formCategoryPlaceholder: "Select a category",
    formCountryLabel: "Country",
    formCountryAria: "Country",
    formCountryPlaceholder: "Select a country",
    categoryGroups: FUNDING_ELIGIBILITY_CATEGORY_GROUPS,
    countries: FUNDING_ELIGIBILITY_COUNTRIES,
  },
  terms: {
    headingMobile: "Our terms are tailored to how your business operates",
    headingDesktop:
      "Choose terms that are tailored to how your business operates.",
    pricingTitle: "Simple, transparent pricing",
    pricingDescription:
      "You know the full cost before you accept. No hidden charges or complicated fee structures.",
    repaymentTitle: "Choose how you repay",
    repaymentDescription:
      "Pick a bi-weekly or monthly repayment schedule. Repay in fixed amounts or as a percentage of sales.",
    equityLead: "No equity taken.",
    equityRest: "We don't take equity, so you keep full control.",
    spendLead: "No spend restrictions.",
    spendRest: "Use funds however your business needs.",
    guaranteesLead: "No personal guarantees.",
    guaranteesRest: "We fund the business, not the person.",
    mockFinancingAmountLabel: "Financing amount",
    mockFeeLabel: "Fee",
    mockTotalLabel: "Total to repay",
    mockBusinessFundingLabel: "Business funding",
    mockNoPersonalLiabilityLabel: "No personal liability",
    mockFinancingAmount: "$100,000",
    mockFeeAmount: "$4,200",
    mockTotalAmount: "$104,200",
  },
  underwriting: {
    heading: "Our underwriting engine uses data to fund your true potential.",
    subheading: "Here's how it works.",
    steps: [
      {
        number: "01",
        eyebrow: "01 - Performance data in",
        title:
          "Securely connect to the Wayflyer platform so our proprietary models can assess your business performance in minutes. This creates a real-time picture of your financial health.",
      },
      {
        number: "02",
        eyebrow: "02 - Underwriting decision made",
        title:
          "Get approved for a financing amount in hours. Our financing experts act as an important human-in-the-loop, with verbal discussions for businesses above a certain size.",
      },
      {
        number: "03",
        eyebrow: "03 - Capital out",
        title:
          "Choose terms that are tailored to how your business operates. Having seen thousands of businesses in action, we understand the different funding needs and can adjust terms accordingly.",
      },
    ],
  },
  faqTitle: "Frequently asked questions",
  faqs: [
    {
      question: "What is Wayflyer?",
      answer:
        "Wayflyer is a financing partner for fast-growing businesses across eCommerce, retail, software & services. Since launching in 2020, we've deployed over $7 billion to more than 7,000 businesses. Our data-led underwriting model means we make financing decisions in hours, not weeks - a speed traditional finance providers can't match.",
    },
    {
      question: "Who does Wayflyer fund?",
      answer:
        "Wayflyer funds growing businesses of all kinds: eCommerce, Amazon sellers, wholesalers, retailers, software / SaaS and services businesses, such as restaurants, consultancies, agencies, gyms, salons, clinics, practices and trades.",
    },
    {
      question: "What are the eligibility criteria to apply for financing?",
      answer:
        "To apply for Wayflyer financing, your business needs at least 6 months of trading history, $10,000+ in monthly sales and incorporation in a supported country: the US, Canada, UK, Ireland, Spain, Germany, Belgium, the Netherlands, Sweden, Denmark or Australia.",
    },
    {
      question: "Will applying affect my credit score?",
      answer:
        "No. Wayflyer runs a soft credit check when reviewing your application, so applying won't affect your credit score. Financing decisions are based on your business performance, not your personal credit history.",
    },
    {
      question: "How much financing can I access?",
      answer:
        "Wayflyer offers financing from $10,000 to $20 million, tailored to your stage of growth. Whether you're an early-stage business or an established operator, your offer is based on your current business performance.",
    },
    {
      question: "What can I use Wayflyer funding for?",
      answer:
        "There are no spend restrictions: use the funds however your business needs. That includes inventory, marketing, hiring, covering receivables, equipment, refurbishment, opening a new location and everyday cash flow.",
    },
    {
      question: "How quickly can I access funds?",
      answer:
        "Applying to Wayflyer takes less than 10 minutes, with a decision in as little as 24 hours. Most businesses receive funds within hours of accepting their offer.",
    },
    {
      question: "What fees do Wayflyer charge?",
      answer:
        "Wayflyer charges a single fixed fee, agreed upfront before you accept your offer. The fee depends on your business profile and the financing term, and there are no origination, prepayment, late payment or hidden fees. You'll see the exact total repayment amount before you commit.",
    },
    {
      question: "How do repayments work?",
      answer:
        "Wayflyer repayments depend on the structure you choose: fixed installments with a Term Loan, or a percentage of revenue with a Cash Advance. Either way, the total repayment amount is agreed before you accept your offer.",
    },
    {
      question: "What documents do I need to provide?",
      answer:
        "No lengthy paperwork. You connect your sales data and bank account so Wayflyer can review your business performance. For larger financing amounts, we may also ask for financial accounts.",
    },
  ],
  footerCta: {
    title: "The future of financing is available today.",
    primaryCtaLabel: "Apply now",
    secondaryCtaLabel: "Speak to us first",
    secondaryCtaPath: "/contact-us",
  },
};
